import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Linkedin, Globe, ArrowLeft } from 'lucide-react';
import SEO from '../components/SEO';
import { AUTHOR, AUTHOR_URL, GUIDES, HELP_ADVICE_INDEX_URL } from '../data/guides';

/**
 * Expert Author profile — E-E-A-T / GEO pillar. Every guide links here and
 * every guide's Article JSON-LD points its `author.@id` at #person on this
 * page, so answer-engines can canonicalise the founder's byline across the
 * whole editorial section.
 */
const ExpertAuthorPage = () => {
  const siteOrigin = typeof window !== 'undefined' ? window.location.origin : 'https://drtoolsuk.com';
  const canonical = `${siteOrigin}${AUTHOR_URL}`;

  const personJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Person',
    '@id': `${canonical}#person`,
    name: AUTHOR.name,
    url: canonical,
    image: `${siteOrigin}${AUTHOR.image}`,
    jobTitle: AUTHOR.role,
    description: AUTHOR.bio,
    email: `mailto:${AUTHOR.email}`,
    worksFor: { '@type': 'Organization', name: 'Dr Tools UK', url: 'https://drtoolsuk.com' },
    sameAs: Object.values(AUTHOR.links).filter(Boolean),
  };
  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${siteOrigin}/` },
      { '@type': 'ListItem', position: 2, name: 'Help & Advice', item: `${siteOrigin}${HELP_ADVICE_INDEX_URL}` },
      { '@type': 'ListItem', position: 3, name: AUTHOR.name, item: canonical },
    ],
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-8" data-testid="expert-author-page">
      <SEO
        title={`${AUTHOR.name} — ${AUTHOR.role} at Dr Tools UK`}
        description={AUTHOR.short_bio}
        canonical={canonical}
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(personJsonLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }} />

      <nav className="text-xs text-neutral-500 mb-4" aria-label="Breadcrumb">
        <Link to="/" className="hover:text-[#F46A25]">Home</Link>
        <span className="mx-1">/</span>
        <Link to={HELP_ADVICE_INDEX_URL} className="hover:text-[#F46A25]">Help &amp; Advice</Link>
        <span className="mx-1">/</span>
        <span className="text-[#12263A] font-semibold">{AUTHOR.name}</span>
      </nav>

      <Link to={HELP_ADVICE_INDEX_URL} className="inline-flex items-center gap-1 text-sm text-[#F46A25] font-bold hover:underline mb-4">
        <ArrowLeft className="w-4 h-4" /> All guides
      </Link>

      <div className="flex flex-col md:flex-row gap-6 items-start mb-8">
        <img src={AUTHOR.image} alt={AUTHOR.name} width="140" height="140" className="w-32 h-32 md:w-36 md:h-36 rounded-full bg-white border border-neutral-200 object-contain p-3" />
        <div className="flex-1">
          <div className="text-[10px] text-[#F46A25] font-bold uppercase tracking-wider">Author profile</div>
          <h1 className="text-3xl md:text-4xl font-black text-[#12263A]">{AUTHOR.name}</h1>
          <div className="text-neutral-500 font-semibold">{AUTHOR.role}</div>
          <div className="flex items-center flex-wrap gap-3 mt-3 text-sm">
            <a href={`mailto:${AUTHOR.email}`} className="flex items-center gap-1 text-neutral-600 hover:text-[#F46A25]"><Mail className="w-3.5 h-3.5" /> {AUTHOR.email}</a>
            {AUTHOR.links.linkedin && <a href={AUTHOR.links.linkedin} rel="noopener noreferrer" target="_blank" className="flex items-center gap-1 text-neutral-600 hover:text-[#F46A25]"><Linkedin className="w-3.5 h-3.5" /> LinkedIn</a>}
            {AUTHOR.links.website && <a href={AUTHOR.links.website} rel="noopener noreferrer" target="_blank" className="flex items-center gap-1 text-neutral-600 hover:text-[#F46A25]"><Globe className="w-3.5 h-3.5" /> Website</a>}
          </div>
        </div>
      </div>

      <section className="prose prose-neutral max-w-none mb-8">
        <h2 className="text-xl font-black text-[#12263A] mb-2">Background</h2>
        <p className="text-neutral-700 leading-relaxed">{AUTHOR.bio}</p>

        <h2 className="text-xl font-black text-[#12263A] mt-6 mb-2">Credentials</h2>
        <ul className="list-disc pl-6 space-y-1 text-neutral-700">
          {AUTHOR.credentials.map((c, i) => <li key={i}>{c}</li>)}
        </ul>
      </section>

      <section className="border-t border-neutral-200 pt-6">
        <h2 className="text-xl font-black text-[#12263A] mb-4">Guides by {AUTHOR.name}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {GUIDES.map(g => (
            <Link key={g.slug} to={`/help-advice/${g.slug}`} data-testid={`author-guide-${g.slug}`} className="flex gap-3 border border-neutral-200 hover:border-[#F46A25] rounded-sm p-3 transition-colors">
              <img src={g.hero_image} alt={g.title} width="120" height="90" className="w-28 h-20 object-cover rounded-sm flex-shrink-0" />
              <div className="min-w-0">
                <div className="text-[10px] text-[#F46A25] font-bold uppercase">{g.category}</div>
                <div className="font-bold text-[#12263A] text-sm leading-tight line-clamp-2">{g.title}</div>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
};

export default ExpertAuthorPage;
